import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const IdentityVerificationStep = ({ formData, onUpdate, onNext, onBack }) => {
  const [errors, setErrors] = useState({});
  const [previewFront, setPreviewFront] = useState(formData?.driversLicenseFront || null);
  const [previewBack, setPreviewBack] = useState(formData?.driversLicenseBack || null);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.driversLicenseFront) {
      newErrors.driversLicenseFront = 'Driver\'s License front photo is required';
    }

    if (!formData?.driversLicenseBack) {
      newErrors.driversLicenseBack = 'Driver\'s License back photo is required';
    }

    if (!formData?.ssn?.trim()) {
      newErrors.ssn = 'Social Security Number is required';
    } else if (!/^\d{3}-?\d{2}-?\d{4}$/?.test(formData?.ssn)) {
      newErrors.ssn = 'Invalid SSN format (XXX-XX-XXXX)';
    }

    if (!formData?.fatherMiddleName?.trim()) {
      newErrors.fatherMiddleName = 'Father\'s Middle Name is required';
    }

    if (!formData?.motherMiddleName?.trim()) {
      newErrors.motherMiddleName = 'Mother\'s Middle Name is required';
    }

    if (!formData?.placeOfBirth?.trim()) {
      newErrors.placeOfBirth = 'Place of Birth is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const formatSSN = (value) => {
    const numbers = value?.replace(/\D/g, '');
    if (numbers?.length <= 3) return numbers;
    if (numbers?.length <= 5) return `${numbers?.slice(0, 3)}-${numbers?.slice(3)}`;
    return `${numbers?.slice(0, 3)}-${numbers?.slice(3, 5)}-${numbers?.slice(5, 9)}`;
  };

  const handleSSNChange = (e) => {
    const formatted = formatSSN(e?.target?.value);
    onUpdate({ ...formData, ssn: formatted });
    if (errors?.ssn) {
      setErrors({ ...errors, ssn: '' });
    }
  };

  const handleFileChange = (e, side) => {
    const file = e?.target?.files?.[0];
    if (file) {
      // Validate file type
      if (!file?.type?.startsWith('image/')) {
        setErrors({ ...errors, [`driversLicense${side}`]: 'Please upload an image file' });
        return;
      }

      // Validate file size (max 5MB)
      if (file?.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, [`driversLicense${side}`]: 'File size must be less than 5MB' });
        return;
      }

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        if (side === 'Front') {
          setPreviewFront(reader?.result);
          onUpdate({ ...formData, driversLicenseFront: reader?.result });
        } else {
          setPreviewBack(reader?.result);
          onUpdate({ ...formData, driversLicenseBack: reader?.result });
        }
        // Clear error for this field
        if (errors?.[`driversLicense${side}`]) {
          setErrors({ ...errors, [`driversLicense${side}`]: '' });
        }
      };
      reader?.readAsDataURL(file);
    }
  };

  const removePhoto = (side) => {
    if (side === 'Front') {
      setPreviewFront(null);
      onUpdate({ ...formData, driversLicenseFront: null });
    } else {
      setPreviewBack(null);
      onUpdate({ ...formData, driversLicenseBack: null });
    }
  };

  const PhotoUploadField = ({ label, side, preview, error }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-[var(--color-foreground)]">
        {label} <span className="text-red-500">*</span>
      </label>
      
      {!preview ? (
        <div className="relative">
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e, side)}
            className="hidden"
            id={`license-${side?.toLowerCase()}`}
          />
          <label
            htmlFor={`license-${side?.toLowerCase()}`}
            className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[var(--color-border)] rounded-lg cursor-pointer hover:border-[var(--color-primary)] transition-colors bg-[var(--color-background)]"
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Icon name="Upload" size={32} color="var(--color-muted-foreground)" className="mb-3" />
              <p className="mb-2 text-sm text-[var(--color-muted-foreground)]">
                <span className="font-semibold">Click to upload</span> or drag and drop
              </p>
              <p className="text-xs text-[var(--color-muted-foreground)]">PNG, JPG or JPEG (MAX. 5MB)</p>
            </div>
          </label>
        </div>
      ) : (
        <div className="relative w-full h-40 border-2 border-[var(--color-border)] rounded-lg overflow-hidden">
          <img
            src={preview}
            alt={`Driver's License ${side}`}
            className="w-full h-full object-cover"
          />
          <button
            type="button"
            onClick={() => removePhoto(side)}
            className="absolute top-2 right-2 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={16} color="white" />
          </button>
        </div>
      )}
      
      {error && (
        <p className="text-sm text-red-500 mt-1">{error}</p>
      )}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      <div className="bg-[var(--color-card)] rounded-lg p-4 md:p-6 lg:p-8 border border-[var(--color-border)]">
        <div className="flex items-start gap-3 mb-4 md:mb-5 lg:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
            <Icon name="Shield" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl lg:text-2xl font-semibold text-[var(--color-foreground)]">
              Identity Verification
            </h3>
            <p className="text-sm md:text-base text-[var(--color-muted-foreground)] mt-1">
              Provide your identity information for secure account verification
            </p>
          </div>
        </div>

        <div className="bg-[var(--color-accent)]/10 border border-[var(--color-accent)]/20 rounded-lg p-4 mb-6">
          <div className="flex gap-3">
            <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-[var(--color-foreground)]">
              <p className="font-medium mb-1">Why we need this:</p>
              <ul className="list-disc list-inside space-y-1 text-[var(--color-muted-foreground)]">
                <li>Comply with financial regulations (KYC/AML)</li>
                <li>Protect your account from unauthorized access</li>
                <li>Enable secure cryptocurrency transactions</li>
                <li>Your data is encrypted and stored securely</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5 lg:space-y-6">
          <PhotoUploadField
            label="Driver's License (Front)"
            side="Front"
            preview={previewFront}
            error={errors?.driversLicenseFront}
          />

          <PhotoUploadField
            label="Driver's License (Back)"
            side="Back"
            preview={previewBack}
            error={errors?.driversLicenseBack}
          />

          <Input
            label="Social Security Number (SSN)"
            type="text"
            placeholder="XXX-XX-XXXX"
            value={formData?.ssn || ''}
            onChange={handleSSNChange}
            error={errors?.ssn}
            maxLength={11}
            required
          />

          <Input
            label="Father's Middle Name"
            type="text"
            placeholder="Enter your father's middle name"
            value={formData?.fatherMiddleName || ''}
            onChange={(e) => {
              onUpdate({ ...formData, fatherMiddleName: e?.target?.value });
              if (errors?.fatherMiddleName) {
                setErrors({ ...errors, fatherMiddleName: '' });
              }
            }}
            error={errors?.fatherMiddleName}
            required
          />

          <Input
            label="Mother's Middle Name"
            type="text"
            placeholder="Enter your mother's middle name"
            value={formData?.motherMiddleName || ''}
            onChange={(e) => {
              onUpdate({ ...formData, motherMiddleName: e?.target?.value });
              if (errors?.motherMiddleName) {
                setErrors({ ...errors, motherMiddleName: '' });
              }
            }}
            error={errors?.motherMiddleName}
            required
          />

          <Input
            label="Place of Birth"
            type="text"
            placeholder="City, State/Province, Country"
            value={formData?.placeOfBirth || ''}
            onChange={(e) => {
              onUpdate({ ...formData, placeOfBirth: e?.target?.value });
              if (errors?.placeOfBirth) {
                setErrors({ ...errors, placeOfBirth: '' });
              }
            }}
            error={errors?.placeOfBirth}
            required
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 md:gap-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="w-full sm:w-auto order-2 sm:order-1"
        >
          <Icon name="ArrowLeft" size={18} />
          Back
        </Button>
        <Button
          type="submit"
          className="w-full sm:flex-1 order-1 sm:order-2"
        >
          Continue
          <Icon name="ArrowRight" size={18} />
        </Button>
      </div>
    </form>
  );
};

export default IdentityVerificationStep;